import 'login/components/LoginBanner/LoginBanner.scss';

import React, { lazy, Suspense } from 'react';
import PropTypes from 'prop-types';

import { LocaleService, UrlService, WindowProxy } from '@sm360/widget-commons-core-services';
import {
    NoSSR,
    EVENTS as EVENTS_COMMONS_GENERAL,
    ModalService,
    AccountService,
    AnalyticsService,
    DATA_LAYER_VARIABLES as DATA_LAYER_VARS,
} from '@sm360/widget-commons-general';
import { TradeInsService } from '@sm360/widget-commons-vehicle';
import { isFunction } from 'lodash';

import TransactionSummary from 'login/components/Transaction/TransactionSummary/TransactionSummary.jsx';

import MessageBanner from 'login/components/Banners/MessageBanner.jsx';
import LoginMenu from 'login/components/LoginMenu/LoginMenu.jsx';

import EVENTS from 'login/constants/events.js';
import URL_PARAMS from 'login/constants/urlParams.js';
import { SIGN_UP, LOGIN, RESET_PASSWORD } from 'login/constants/modalNames.js';
import TAGS from 'login/constants/trackingTags';

// The webpackChunkName name here is intended to be public. Webpack will use it to name files in production and development modes.
const Modals = lazy(() => import(/* webpackChunkName: "Modals" */ './Modals.jsx'));

class LoginBanner extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            isAuthenticated: false,
            vehicleTradein: {
                tradeId: '',
                make: '',
                model: '',
                modelTrim: '',
                year: '',
                odometer: '',
                retail: '',
                amount: 0,
            },
        };

        this.onRegister = this.onRegister.bind(this);
        this.onLogin = this.onLogin.bind(this);
        this.onAuthentication = this.onAuthentication.bind(this);
        this.getVehicleTradeIn = this.getVehicleTradeIn.bind(this);
        this.handleDeleteTradeIn = this.handleDeleteTradeIn.bind(this);
    }

    componentDidMount() {
        const $window = WindowProxy.get();

        if ($window) {
            $window.addEventListener(EVENTS.AUTHENTICATED, this.onAuthentication);
            $window.addEventListener(EVENTS.SHOW_LOGIN_MODAL, ModalService.get(LOGIN).open);
            $window.addEventListener(EVENTS.SHOW_REGISTER_MODAL, ModalService.get(SIGN_UP).open);
            $window.document.addEventListener(EVENTS_COMMONS_GENERAL.TRADEIN_CREATED, this.getVehicleTradeIn);
        }
        const resetCode = UrlService.getUrlParameter(URL_PARAMS.RESET_CODE);

        if (resetCode) {
            setTimeout(() => ModalService.get(RESET_PASSWORD).open(), 1500);
        }

        const isAuthenticated = AccountService.isAuthenticated();
        if (isAuthenticated) {
            // eslint-disable-next-line react/no-did-mount-set-state
            this.setState({ isAuthenticated: true }, this.getVehicleTradeIn);
            AnalyticsService.setDataLayerVariable(DATA_LAYER_VARS.IS_LOGGEDIN, true);
        }
    }

    componentWillUnmount() {
        const $window = WindowProxy.get();

        if ($window) {
            $window.removeEventListener(EVENTS.AUTHENTICATED, this.onAuthentication);
            $window.removeEventListener(EVENTS.SHOW_LOGIN_MODAL, ModalService.get(LOGIN).open);
            $window.removeEventListener(EVENTS.SHOW_REGISTER_MODAL, ModalService.get(SIGN_UP).open);
            $window.document.removeEventListener(EVENTS_COMMONS_GENERAL.TRADEIN_CREATED, this.getVehicleTradeIn);
        }
    }

    onAuthentication() {
        this.setState({ isAuthenticated: AccountService.isAuthenticated() }, this.getVehicleTradeIn);
        AnalyticsService.setDataLayerVariable(DATA_LAYER_VARS.IS_LOGGEDIN, true);
    }

    onRegister() {
        AnalyticsService.gaTrigger(TAGS.LOGIN_WIDGET__REGISTER());
        AnalyticsService.gaTrigger(TAGS.MY_TRANSACTION__CREATE_AN_ACCOUNT(WindowProxy.location().pathname));

        const modal = ModalService.get(SIGN_UP);
        modal.open({ context: 'widget.login.signup.information' });
    }

    onLogin(onClose) {
        AnalyticsService.gaTrigger(TAGS.LOGIN_WIDGET__LOGIN());
        AnalyticsService.gaTrigger(TAGS.MY_TRANSACTION__LOGIN(WindowProxy.location().pathname));
        const modal = ModalService.get(LOGIN);
        modal.addCallbacks({
            onClose: () => {
                if (isFunction(onClose)) {
                    onClose();
                }
                return true;
            },
        });
        modal.open({ context: 'widget.login.signin.information' });
    }

    getVehicleTradeIn() {
        if (this.state.isAuthenticated) {
            TradeInsService.loadTradeInVehicle().then((tradeIn) => {
                if (tradeIn) {
                    const vehicleTradein = {
                        tradeId: tradeIn.id,
                        make: tradeIn.make,
                        model: tradeIn.model,
                        modelTrim: tradeIn.trim,
                        year: tradeIn.year,
                        odometer: tradeIn.kilometers,
                        retail: tradeIn.estimation.amount,
                        amount: tradeIn.estimation.amount,
                    };
                    // Setting up tradeIn to localstorage to use on other widgets and v2.5 builder
                    const windowProxy = WindowProxy.get();
                    if (windowProxy && windowProxy.localStorage) {
                        windowProxy.localStorage.setItem('tradeIn', parseInt(tradeIn.estimation.amount, 10));
                    }
                    this.setState({
                        vehicleTradein,
                    });
                }
            });
        }
    }

    /* Delete TradeIn
    * Call tradeIn microservice to delete tradeIn and update the quote
    * set vehicleTradein state to an empty
    * set tradeIn localstorage key to 0
    */
    handleDeleteTradeIn() {
        const windowProxy = WindowProxy.get();
        TradeInsService.deleteTradeIn(this.state.vehicleTradein).then(() => {
            this.setState({
                vehicleTradein: {},
            });
            if (windowProxy) {
                windowProxy.localStorage.setItem('tradeIn', 0);
            }
        });
    }

    render() {
        const { isNewEnabled, isUsedEnabled, usedUrl } = this.props;
        const buttonView = (
            <div className="login-content">
                <button
                    id="button-login"
                    className="btn-login button secondary"
                    onClick={this.onLogin}
                    data-test="login.sign-in.open"
                >
                    <icon className="icon-login" />
                    <span className="label">{LocaleService.t('LOGIN.LOGIN_BANNER.LOGIN')}</span>
                </button>
                <button
                    id="button-register"
                    className="btn-register button sr-button-1 is-small"
                    onClick={this.onRegister}
                    data-test="login.sign-up.open"
                >
                    <span className="label">{LocaleService.t('LOGIN.LOGIN_BANNER.GET_STARTED')}</span>
                </button>
            </div>
        );

        const menuView = (
            <div className="login-content login-authenticated ">
                <LoginMenu />
            </div>
        );

        return (
            <div
                id="LoginBanner"
                className="LoginBanner widget-login-desktop widget-login-desktop-reset sr-topbar-login"
            >
                <div className="LoginBannerWrapper clearfix">
                    <MessageBanner text={this.props.messageBanner.text} level={this.props.messageBanner.level} />

                    <TransactionSummary
                        isNewEnabled={isNewEnabled}
                        isUsedEnabled={isUsedEnabled}
                        usedUrl={usedUrl}
                        vehicleTradein={this.state.vehicleTradein}
                        isAuthenticated={this.state.isAuthenticated}
                        loginClick={this.onLogin}
                        deleteTradeIn={this.handleDeleteTradeIn}
                    />

                    {this.state.isAuthenticated ? menuView : buttonView}

                    <NoSSR>
                        <Suspense fallback="">
                            <Modals />
                        </Suspense>
                    </NoSSR>
                </div>
            </div>
        );
    }
}

LoginBanner.defaultProps = {
    isNewEnabled: true,
    isUsedEnabled: true,
    usedUrl: '',
};

LoginBanner.propTypes = {
    messageBanner: PropTypes.shape({
        text: PropTypes.string.isRequired,
        level: PropTypes.string.isRequired,
    }).isRequired,
    isNewEnabled: PropTypes.bool,
    isUsedEnabled: PropTypes.bool,
    usedUrl: PropTypes.string,
};

LoginBanner.displayName = 'LoginBanner';

export default LoginBanner;
